import { useEffect, useRef, useState } from 'react';
import { StudentSignUpForm } from '../student-discount/StudentSignupForm';
import StudentVerificationForm from './student-verification-form';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { isSayWeeeApp } from '@/utils/common/device';
import StudentBenefitsPopup from './student-benefits-popup';
import Trans from 'next-translate/Trans';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { getReferralIdFromCookie } from '@/utils/common/referral';
import { ClickType } from '@/constants/track';
import { CroppedImage } from '@weee-fe/module';
import callAppFunction from '@/utils/bridge';
import { buildUrlWithOrigin } from '@/utils/url';
import { InfoOutline } from '@weee-fe/kunlunicon';
import { twMerge } from 'tailwind-merge';
import { registerToAppsFlyerInApp } from '@/utils/ga-data-manager';

const STUDENT_BENEFITS_WS = 'student_hub';

const PreVerification = props => {
  const { isPC, data = {} } = props;
  const { navigation_bar, is_login } = data;
  const { lang, t } = useTranslation('student');
  const router = useRouter();
  const benefitsRef = useRef<any>(null);
  const { buttonClick } = useClickActionT2();

  const leftProductImg = navigation_bar?.images?.[0] || '';
  const rightProductImg = navigation_bar?.images?.[1] || '';

  const onSuccess = async res => {
    // 完成登录后，通知app端登录成功
    if (res?.token && isSayWeeeApp()) {
      try {
        await callAppFunction('accountStatusNotify', {
          type: 'login',
          token: res?.token
        });
        setTimeout(() => {
          registerToAppsFlyerInApp();
        }, 300);
      } catch (e) {
        console.log('notifyAppToUpdateAccountStatus error', e);
      }
    }
    benefitsRef.current?.show();
  };

  const handleToLogin = () => {
    buttonClick({
      co: {
        target_nm: 'login',
        click_type: ClickType.normal
      },
      ctx: {
        user_source: getReferralIdFromCookie() ? 'referred' : 'normal'
      }
    });

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('ws');
    const cleanedRedirectUrl = currentUrl.toString();
    // redirect_url参数位置需要在login?之后
    const loginUrl = `/${lang}/account/login?redirect_url=${encodeURIComponent(
      cleanedRedirectUrl
    )}&ws=${STUDENT_BENEFITS_WS}`;

    if (isSayWeeeApp() || isPC) {
      window.location.href = buildUrlWithOrigin(loginUrl);
    } else {
      router.push(loginUrl);
    }
  };

  return (
    <div>
      <div className="relative w-full overflow-hidden">
        {/* 左侧装饰图 */}
        <div className="absolute top-0 -left-[69px] w-[110px] aspect-square z-10 pointer-events-none rotate-[26deg]">
          <div className="relative w-full h-full aspect-square rounded-full bg-[#9CE7F1]">
            {leftProductImg && (
              <CroppedImage
                url={leftProductImg}
                alt=""
                configs={{ renderWidth: 131 }}
                lazyLoad={false}
                placeholder={<div className="w-[131px] h-[131px] bg-surface-100-bg rounded-full opacity-5 skeleton" />}
                className="absolute top-0 left-2.5 z-10 w-[131px] aspect-square object-contain"
              />
            )}
          </div>
        </div>

        {/* 右侧装饰图 */}
        <div className="absolute top-[50px] -right-[63px] w-[98px] aspect-square z-10 pointer-events-none rotate-[-34deg]">
          <div className="relative w-full h-full aspect-square rounded-full bg-[#FF96E8]">
            {rightProductImg && (
              <CroppedImage
                url={rightProductImg}
                alt=""
                configs={{ renderWidth: 116 }}
                lazyLoad={false}
                placeholder={<div className="w-[116px] h-[116px] bg-surface-100-bg rounded-full opacity-5 skeleton" />}
                className="absolute -top-0 right-5 z-10 w-[116px] aspect-square object-contain"
              />
            )}
          </div>
        </div>

        <div className="relative z-20 max-w-[350px] mx-auto flex flex-col items-center px-5 pt-1 pb-2">
          {/* 优惠金额标题区域 */}
          <div className="text-center text-primary-1">
            <h2 className="enki-body-sm-strong uppercase">{navigation_bar?.content?.sub_title}</h2>
            <div className="enki-display-5xl-strong">{navigation_bar?.content?.content}</div>
          </div>

          {/* 权益列表区域 */}
          <div className="w-full my-5">
            <div className="flex items-center justify-center">
              <span className="h-[1px] w-16 bg-primary-1"></span>
              <span className="mx-2 enki-body-base text-primary-1">{navigation_bar?.content?.desc}</span>
              <span className="h-[1px] w-16 bg-primary-1"></span>
            </div>

            <ul className="list-none text-center">
              {data?.benefits_list?.map((item, index) => (
                <li key={index} className="mt-2 enki-display-lg-strong text-primary-1">
                  {item?.content?.title}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="px-5 pb-6 max-w-[350px] mx-auto">
        {is_login ? (
          <StudentVerificationForm
            {...props}
            formStyle="light"
            reqParams={{ source: STUDENT_BENEFITS_WS }}
            onSuccess={onSuccess}
            clickTrack={() => {
              buttonClick({
                co: {
                  target_nm: 'verify_student',
                  click_type: ClickType.normal
                },
                ctx: {
                  user_source: getReferralIdFromCookie() ? 'referred' : 'normal'
                }
              });
            }}
          />
        ) : (
          <StudentSignUpForm
            {...props}
            formStyle="light"
            entry="student-benefits"
            reqParams={{ source: STUDENT_BENEFITS_WS }}
            onSuccess={onSuccess}
            clickTrack={() => {
              buttonClick({
                co: {
                  target_nm: 'sign_up',
                  click_type: ClickType.normal
                },
                ctx: {
                  user_source: getReferralIdFromCookie() ? 'referred' : 'normal'
                }
              });
            }}
          />
        )}

        <div className="text-center my-4 enki-body-3xs text-surface-100-fg-minor px-5">
          <Trans
            i18nKey={is_login ? 'student:by_clicking_verify' : 'student:by_clicking_create'}
            components={{
              terms: (
                <a
                  className="text-surface-100-fg-default cursor-pointer"
                  href={`/${lang}/about/terms-of-service?pushto=native`}
                />
              ),
              program: (
                <a
                  className="text-surface-100-fg-default cursor-pointer"
                  href={`/${lang}/help/detail/2681?pushto=native`}
                />
              ),
              policy: (
                <a
                  className="text-surface-100-fg-default cursor-pointer"
                  href={`/${lang}/about/privacy-policy?pushto=native`}
                />
              ),
              br: <br />
            }}
          />
        </div>

        {!is_login && (
          <div className="text-center">
            <a className="enki-body-2xs-medium text-link-base-1" onClick={handleToLogin}>
              {t('have_account')}
            </a>
          </div>
        )}
        <div
          className={twMerge(
            'bg-surface-200-bg p-3 enki-body-xs text-surface-100-fg-minor rounded-300 flex items-center',
            !is_login && 'mt-8'
          )}>
          <InfoOutline fontSize={16} className="mr-2" />
          {t('new_student_exclusive_perks')}
        </div>
        <StudentBenefitsPopup ref={benefitsRef} isPC={isPC} />
      </div>
    </div>
  );
};

export default PreVerification;
